/**
 * PeopleList — flat list of people on a RAO cart.
 *
 * Wave 1 #2.6 (Bill 2026-04-28): the prior layout grouped people into
 * Primary / Additional / Relationships sections keyed off each person's
 * `role` field. That model fights the OwnershipStep matrix where role is
 * per-product (a person can be Primary on a DDA and Joint on a SAV in the
 * same cart). Pre-delineating ownership level at PeopleStep is structurally
 * wrong — removed.
 *
 * Canonical SPFx shape (PeopleScreen.tsx): a flat `cart.people.map(...)`
 * badge list. People are added without a role; ownership-level assignment
 * happens entirely on the OwnershipStep matrix.
 *
 * Render contract:
 *   - One `PersonRow` per cart.people entry, in cart order
 *   - Empty state when 0 people
 *   - Edit + Remove actions surfaced inline (no role-keyed sections)
 *   - Single "+ Add Person" entry point lives on PeopleStep, NOT here
 */
import { Button, EntityIcon, Pill } from '@/components/primitives';
import { formatSsn } from '@/utils/ssn';
import { initials, formatIsoDate } from '../utils/formatting';
import {
  isExistingCustomer,
  deriveVerifyTabStatus,
  DEFAULT_ID_EXPIRATION_WARNING_DAYS,
} from './personDialog/shared/existingCustomer';
import type { RaoPerson } from '../types';

export interface PeopleListProps {
  people: ReadonlyArray<RaoPerson>;
  onEditPerson?: (p: RaoPerson) => void;
  onRemovePerson?: (p: RaoPerson) => void;
  disabled?: boolean;
  /** People shown without Edit / Remove (account maintenance: the existing owners come from Core). */
  lockedIds?: ReadonlyArray<string>;
}

function cipPill(person: RaoPerson): JSX.Element | null {
  // Existing customers carry a prior-verified CIP baseline — CIP-EXPIRATION is
  // the only standing gate (Bill v0.70.0 walk / BACKLOG-0835). A picked CustSrch
  // hit whose cipStatus never got set to 'clear' was falsely painting "CIP
  // pending"; judge it on ID expiry instead of the raw status.
  if (isExistingCustomer(person)) {
    // C2: expired-within-grace (default 6mo, Collette) now derives yellow rather
    // than red — the red pill is reserved for the true "Required" hard-stop.
    const v = deriveVerifyTabStatus(person, DEFAULT_ID_EXPIRATION_WARNING_DAYS);
    if (v.status === 'red') return <Pill variant="err">CIP — ID expired</Pill>;
    if (v.status === 'yellow') {
      if (/update recommended/i.test(v.reason)) return <Pill variant="warn">CIP — update recommended</Pill>;
      if (/expir/i.test(v.reason)) return <Pill variant="warn">CIP — ID expiring</Pill>;
    }
    return <Pill variant="ok">CIP on file</Pill>;
  }
  switch (person.cipStatus) {
    case 'clear':     return <Pill variant="ok">✓ CIP clear</Pill>;
    case 'pending':   return <Pill variant="warn">CIP pending</Pill>;
    case 'exception': return <Pill variant="err">CIP exception</Pill>;
    default:          return null;
  }
}

function PersonRow({
  person,
  onEdit,
  onRemove,
  disabled,
}: {
  person: RaoPerson;
  onEdit?: (p: RaoPerson) => void;
  onRemove?: (p: RaoPerson) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="rao-person-row">
      <span className="rao-person-avatar" aria-hidden>{initials(person.fullName)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="rao-person-name">
          {person.fullName || '(unnamed)'}
          {person.customerType && (
            <span style={{ marginLeft: 'var(--sp-3)' }}>
              <Pill variant="muted">{person.customerType}</Pill>
            </span>
          )}
          {person.relCode && (
            <span style={{ marginLeft: 'var(--sp-2)' }}>
              <EntityIcon type="relationship" code={person.relCode} />
            </span>
          )}
        </div>
        <div className="rao-person-meta">
          {person.dob ? `DOB ${formatIsoDate(person.dob)} · ` : ''}
          SSN {formatSsn(person.taxId ?? undefined)}
          {person.cifId ? ` · CIF ${person.cifId}` : ''}
        </div>
      </div>
      {/* One action cluster (Bill 2026-10-16, "edit and remove cart person controls ... spilling"): the row grid had
          4 columns for up to 5 children, so with a CIP pill showing, Remove wrapped onto its own line under the
          avatar. The pill and buttons now stay together, and drop under the details on a narrow panel. */}
      <div className="rao-person-actions">
        {cipPill(person)}
        {onEdit && !disabled && (
          <Button variant="ghost" size="sm" onClick={() => onEdit(person)} aria-label={`Edit ${person.fullName || 'person'}`}>
            Edit
          </Button>
        )}
        {onRemove && !disabled && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(person)} aria-label={`Remove ${person.fullName || 'person'}`}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

export function PeopleList({
  people,
  onEditPerson,
  onRemovePerson,
  disabled = false,
  lockedIds,
}: PeopleListProps): JSX.Element {
  if (people.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-10) var(--sp-7)',
          textAlign: 'center',
          border: '2px dashed var(--ink-200)',
          borderRadius: 'var(--r-md)',
          color: 'var(--ink-600)',
        }}
      >
        <div style={{ fontSize: 14, marginBottom: 'var(--sp-2)', color: 'var(--pcb-blue)' }}>
          No people added yet
        </div>
        <div style={{ fontSize: 12 }}>
          Search for an existing customer or add a new person — each person’s role is set per product on the Products step.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {people.map((p, i) => (
        <PersonRow
          key={p.cifId ?? `${p.fullName}-${i}`}
          person={p}
          onEdit={p.id && lockedIds?.includes(p.id) ? undefined : onEditPerson}
          onRemove={p.id && lockedIds?.includes(p.id) ? undefined : onRemovePerson}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
